import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { colors } from '@styles/colors';
import { fontWeight } from '@styles/helpers';
import { useI18n } from '@i18n/useI18n';
import { useStore } from '@store/appStore';
import { useAuthentication } from '@providers/AuthProvider';
import { CustomButton } from '@components/Pages/ErrorPage/ErrorPage.styled';
import { useEffect } from 'react';
import { reportShownError } from '@utils/reportShownError';

// Shown when the user is signed in but the login token can no longer be renewed silently (Microsoft
// wants them to sign in again) while they're actively using the page - instead of redirecting away
// from under them, or inside an iframe where redirects aren't possible at all. The button opens a
// popup (useAuthBase.signInAgain), so the page and anything typed on it stay as they are.
// Shared with NewVersionPrompt. Filled in a strong colour (orange when action is needed, the app's
// teal for information) with a white button, sliding in and pulsing once so it isn't missed - a
// white bar with the usual teal button blended into the page.
const slideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, 24px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25), 0 0 0 0 var(--prompt-glow); }
  70% { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25), 0 0 0 14px transparent; }
  100% { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25), 0 0 0 0 transparent; }
`;

// A darker orange than colors.orange: white text on that one is only ~2.9:1 contrast, this is ~5.2:1
// (the teal is ~5:1).
const WARNING_COLOR = '#c2410c';

export const PromptBar = styled.div<{ tone?: 'warning' | 'info' }>`
  --prompt-color: ${(props) => (props.tone === 'warning' ? WARNING_COLOR : colors.mainTextColor)};
  --prompt-glow: ${(props) => (props.tone === 'warning' ? 'rgba(194, 65, 12, 0.6)' : 'rgba(39, 121, 130, 0.6)')};

  position: fixed;
  left: 50%;
  /* --bottom-nav-height: the registration footer's buttons, when shown (RegistrationNavFooter) */
  bottom: calc(var(--bottom-nav-height, 0px) + 24px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 100001;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 20px;
  width: max-content;
  max-width: calc(100vw - 32px);
  padding: 16px 24px;
  background: var(--prompt-color);
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  animation: ${slideIn} 0.35s ease-out, ${pulse} 1.6s ease-out 0.35s 2;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  p {
    margin: 0;
    font-size: 17px;
    font-weight: ${fontWeight.semiBold};
    color: ${colors.white};
    text-align: center;
  }

  a {
    margin: 0;
    cursor: pointer;
    background-color: ${colors.white};
    color: var(--prompt-color);
    font-weight: ${fontWeight.bold};
    white-space: nowrap;
  }
`;

export const SessionExpiredPrompt = () => {
  const i18n = useI18n();
  const { signInAgain } = useAuthentication();
  const { sessionExpired, user } = useStore();
  const shown = !!(sessionExpired && user && signInAgain);

  // How often silent token renewal fails for real users - see reportShownError.
  useEffect(() => {
    if (shown) reportShownError('session-expired-prompt');
  }, [shown]);

  if (!shown) return null;

  return (
    <PromptBar role="alert" tone="warning">
      <p>{i18n.t('session-expired-text')}</p>
      <CustomButton
        small={true}
        mobile={true}
        onClick={(e) => {
          e.preventDefault();
          signInAgain();
        }}
      >
        {i18n.t('session-expired-button')}
      </CustomButton>
    </PromptBar>
  );
};

export default SessionExpiredPrompt;
